import { getAuthToken, setAuthToken } from "./api";

export const NOTES_POPOUT_STOP_MESSAGE = "calltree.notes-popout.stop-stream";
export const NOTES_POPOUT_STOPPED_CONTACT_KEY = "notetaker_stopped_contact_id";

export function stopNotesPopoutStream(targetWindow: Window | null, contactId: string): void {
  if (!targetWindow || targetWindow.closed) return;
  targetWindow.postMessage({ type: NOTES_POPOUT_STOP_MESSAGE, contactId }, window.location.origin);
}

export const NOTES_POPOUT_PATH = "/notepad";
export const NOTES_POPOUT_CONTACT_KEY = "notetaker_contact_id";
export const NOTES_POPOUT_BOOTSTRAP_MESSAGE = "calltree.notes-popout.bootstrap";
export const NOTES_POPOUT_READY_MESSAGE = "calltree.notes-popout.ready";
export const NOTES_POPOUT_C360_STATE_MESSAGE = "calltree.notes-popout.c360-state";
export const NOTES_POPOUT_C360_SEARCH_MESSAGE = "calltree.notes-popout.c360-search";

const NOTES_POPOUT_WINDOW_NAME = "calltree-notepad-window";
const NOTES_TOKEN_KEY = "notetaker_token";

export interface NotesPopoutBootstrapPayload {
  type: typeof NOTES_POPOUT_BOOTSTRAP_MESSAGE;
  contactId: string;
  token: string;
  /** Agent ARN, forwarded so the popout can apply client-side feature
   *  allowlists that depend on the agent identity (no Connect SDK in
   *  the popout window to fetch this directly). */
  agentArn?: string | null;
}

export interface NotesPopoutReadyPayload {
  type: typeof NOTES_POPOUT_READY_MESSAGE;
}

export interface C360StatePayload {
  type: typeof NOTES_POPOUT_C360_STATE_MESSAGE;
  status: string;
  steps: { name: string; event_type: string; timestamp: string }[];
  result: unknown | null;
  error: Record<string, unknown> | null;
}

export interface C360SearchPayload {
  type: typeof NOTES_POPOUT_C360_SEARCH_MESSAGE;
  query: string;
}

export function isC360SearchMessage(data: unknown): data is C360SearchPayload {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === NOTES_POPOUT_C360_SEARCH_MESSAGE
  );
}

export function sendC360SearchToParent(query: string): void {
  window.opener?.postMessage(
    { type: NOTES_POPOUT_C360_SEARCH_MESSAGE, query } satisfies C360SearchPayload,
    window.location.origin,
  );
}

export function isC360StateMessage(data: unknown): data is C360StatePayload {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === NOTES_POPOUT_C360_STATE_MESSAGE
  );
}

export function sendC360StateToPopout(
  targetWindow: Window | null,
  state: Omit<C360StatePayload, "type">,
): void {
  if (!targetWindow || targetWindow.closed) return;
  targetWindow.postMessage(
    { type: NOTES_POPOUT_C360_STATE_MESSAGE, ...state } satisfies C360StatePayload,
    window.location.origin,
  );
}

function getNotesPopoutUrl(contactId: string): string {
  const url = new URL(NOTES_POPOUT_PATH, window.location.origin);
  url.searchParams.set("contactId", contactId);
  return url.toString();
}

function getWindowFeatures(): string {
  const width = 520;
  const height = 900;
  const left = Math.max(window.screenX + window.outerWidth - width - 24, 0);
  const top = Math.max(window.screenY + 48, 0);

  return [
    "popup=yes",
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
  ].join(",");
}

function setPopoutState(targetWindow: Window, contactId: string, token: string): boolean {
  try {
    targetWindow.sessionStorage.setItem(NOTES_TOKEN_KEY, token);
    targetWindow.sessionStorage.setItem(NOTES_POPOUT_CONTACT_KEY, contactId);
    return true;
  } catch {
    return false;
  }
}

export function getRequestedPopoutContactId(): string | null {
  const urlContactId = new URLSearchParams(window.location.search).get("contactId");
  return urlContactId || sessionStorage.getItem(NOTES_POPOUT_CONTACT_KEY);
}

export function getStoredPopoutContactId(): string | null {
  return sessionStorage.getItem(NOTES_POPOUT_CONTACT_KEY);
}

export function setStoredPopoutContactId(contactId: string): void {
  sessionStorage.setItem(NOTES_POPOUT_CONTACT_KEY, contactId);
}

export function clearStoredPopoutContactId(): void {
  sessionStorage.removeItem(NOTES_POPOUT_CONTACT_KEY);
}

export function isNotesPopoutReadyMessage(data: unknown): data is NotesPopoutReadyPayload {
  return typeof data === "object" && data !== null && "type" in data && data.type === NOTES_POPOUT_READY_MESSAGE;
}

export function isNotesPopoutBootstrapMessage(
  data: unknown
): data is NotesPopoutBootstrapPayload {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === NOTES_POPOUT_BOOTSTRAP_MESSAGE &&
    "contactId" in data &&
    typeof data.contactId === "string" &&
    "token" in data &&
    typeof data.token === "string"
  );
}

export function bootstrapNotesPopoutWindow(
  targetWindow: Window,
  contactId: string,
  agentArn?: string | null,
): boolean {
  const token = getAuthToken();
  if (!token || targetWindow.closed) {
    return false;
  }

  setPopoutState(targetWindow, contactId, token);
  targetWindow.postMessage(
    {
      type: NOTES_POPOUT_BOOTSTRAP_MESSAGE,
      contactId,
      token,
      agentArn,
    } satisfies NotesPopoutBootstrapPayload,
    window.location.origin
  );
  return true;
}

export function focusNotesPopout(targetWindow: Window | null): boolean {
  if (!targetWindow || targetWindow.closed) {
    return false;
  }

  targetWindow.focus();
  return true;
}

export function openNotesPopout(contactId: string): Window | null {
  const token = getAuthToken();
  if (!token) {
    return null;
  }

  const targetWindow = window.open("", NOTES_POPOUT_WINDOW_NAME, getWindowFeatures());
  if (!targetWindow) {
    return null;
  }

  setPopoutState(targetWindow, contactId, token);
  targetWindow.location.replace(getNotesPopoutUrl(contactId));
  targetWindow.focus();
  return targetWindow;
}

export function signalNotesPopoutReady(): void {
  window.opener?.postMessage({ type: NOTES_POPOUT_READY_MESSAGE } satisfies NotesPopoutReadyPayload, window.location.origin);
}

export function hydrateNotesPopoutSession(contactId: string, token: string): void {
  setAuthToken(token);
  setStoredPopoutContactId(contactId);
}

